// ============================================================================
// Nombres complexes (moteur v11) : la feuille de brouillon manipule i.
// ============================================================================
// « (1 + i)⁸ = ((1 + i)²)⁴ », « (1 + i)² = 1 + 2i + i² = 1 + 2i − 1 = 2i »,
// « (2i)⁴ = 2⁴ × i⁴ = 16 × 1 = 16 ». Une ligne est une CHAÎNE de membres reliés
// par « = » ; chaque maillon doit être juste (calcul complexe exact : i² = −1)
// ET justifié en un pas reconnaissable :
//   • une identité algébrique (développer, factoriser, regrouper) : vraie quand
//     i est une lettre quelconque — (1 + i)² = 1 + 2i + i² ;
//   • une puissance de i réduite : i² = −1, i⁴ = 1, i³ = −i ;
//   • découper une puissance : z⁸ = (z²)⁴ = z⁴ × z⁴ ; puissance d'un produit :
//     (2i)⁴ = 2⁴ × i⁴ ;
//   • un calcul sur des nombres réels (16 × 1 = 16, (√2)⁸ = 16, cos(2π) = 1) ;
//   • un produit de deux complexes écrits a + bi : (1 + i)² = 2i ;
//   • forme trigonométrique / exponentielle et formule de Moivre ;
//   • remplacer par un résultat déjà établi (une ligne écrite avant).
// Un maillon peut changer plusieurs morceaux à la fois s'ils se justifient
// chacun. Une puissance élevée (> 2) d'un complexe ne se calcule pas d'un bond.
//
// Le but : la cible (`complex.target`) égale à une forme algébrique a + bi
// (a, b nombres) — ou un réel. Le corrigé (`complex.path`) est fourni par le
// modèle et rejoué par la porte avec ce juge. Ce mode est séparé du moteur des
// égalités : il ne change rien aux autres feuilles.
// ============================================================================

import { CONSTS, checkSyntax, formatNumber, normalizeChips, type Chip } from './lexer';
import type { ScratchModel } from './model';
import {
  canon,
  close,
  ev,
  kids,
  lineAtoms,
  lineText,
  parseLine,
  parseText,
  textOf,
  type Eq,
  type Line,
  type Node,
} from './tree';

export interface ComplexSpec {
  target: string;
  path: string[];
  /** forme attendue : algébrique a + bi (défaut) ou exponentielle r e^(iθ) */
  form?: 'algebraic' | 'exponential';
  /** équation en z (`target` = l'équation « (1 + i) × z = 3 − i ») : l'inconnue */
  unknown?: string;
  /** ses solutions (expressions avec i) */
  solutions?: string[];
  /** grandeurs définies par une formule : { Δ: '2^2 − 4 × 1 × 5' } */
  defs?: Record<string, string>;
}

export type Cx = [number, number];

export interface ComplexState {
  target: Node;
  form: 'algebraic' | 'exponential';
  /** valeur de la cible */
  value: Cx;
  /** grandeurs réelles connues (données de l'énoncé) */
  env: CEnv;
  parent: Map<string, string>;
  nodes: Map<string, Node>;
  facts: Eq[];
  /** équation en z : l'inconnue, ses solutions, les équations établies (énoncé + lignes) */
  unknown?: string;
  sols: Cx[];
  eqLines: Line[];
  /** grandeurs définies par une formule (Δ) */
  defs: string[];
}

export interface ComplexVerdict {
  kind:
    | 'empty'
    | 'syntax'
    | 'trap'
    | 'open'
    | 'pending'
    | 'step'
    | 'false'
    | 'unjustified'
    | 'useless';
  atoms: string[];
  title?: string;
  message?: string;
  badChip?: number;
  complete?: boolean;
  line?: Line;
  hora?: number;
  final?: boolean;
  rule?: { id: string; card: string; label: string; hora: number; why: string };
  trap?: { line: Line; title: string; why: string };
  /**
   * noyau (moteur v34) : la ligne est JUSTE mais ne se justifie pas encore en un pas
   * (`jump`), ou ne part pas de la question (`aside`). L'ancien moteur la refuse ; le
   * noyau la garde, sans HORA (D7).
   */
  keep?: { line: Line; kind: 'jump' | 'aside' };
}

/** la lettre de l'unité imaginaire */
export const IMAG = 'i';

// ── calcul complexe ────────────────────────────────────────────────────────

const cadd = (a: Cx, b: Cx): Cx => [a[0] + b[0], a[1] + b[1]];
const cmul = (a: Cx, b: Cx): Cx => [a[0] * b[0] - a[1] * b[1], a[0] * b[1] + a[1] * b[0]];
function cdiv(a: Cx, b: Cx): Cx | null {
  const d = b[0] * b[0] + b[1] * b[1];
  if (d === 0) return null;
  return [(a[0] * b[0] + a[1] * b[1]) / d, (a[1] * b[0] - a[0] * b[1]) / d];
}
function cpowInt(a: Cx, n: number): Cx | null {
  if (!Number.isInteger(n) || Math.abs(n) > 5000) return null;
  let r: Cx = [1, 0];
  let b = a;
  let k = Math.abs(n);
  while (k) {
    if (k & 1) r = cmul(r, b);
    b = cmul(b, b);
    k >>= 1;
  }
  return n < 0 ? cdiv([1, 0], r) : r;
}
const real = (a: Cx | null): number | null => (a && Math.abs(a[1]) < 1e-12 ? a[0] : null);

/**
 * Valeur complexe d'une expression (`i` = unité imaginaire). Les fonctions
 * (cos, sin, ln, √…) ne prennent que des réels ; exp accepte un complexe
 * (forme exponentielle). `imag` remplace i (sert aux identités : i quelconque).
 */
/** grandeurs connues : réelles (données) ou complexes nommées (z = 1 + i, j = e^(2iπ/3)) */
export type CEnv = Record<string, number | Cx>;

export function evC(t: Node, env: CEnv, imag: Cx = [0, 1]): Cx | null {
  const R = (x: Node) => real(evC(x, env, imag));
  switch (t.k) {
    case 'n':
      return [t.v!, 0];
    case 's':
      if (t.n === IMAG) return imag;
      if (env[t.n!] !== undefined) {
        const v = env[t.n!]!;
        return typeof v === 'number' ? [v, 0] : v;
      }
      return CONSTS[t.n!] !== undefined ? [CONSTS[t.n!]!, 0] : null;
    case 'add': {
      let s: Cx = [0, 0];
      for (const c of t.c!) {
        const v = evC(c, env, imag);
        if (!v) return null;
        s = cadd(s, v);
      }
      return s;
    }
    case 'mul': {
      let s: Cx = [1, 0];
      for (const c of t.c!) {
        const v = evC(c, env, imag);
        if (!v) return null;
        s = cmul(s, v);
      }
      return s;
    }
    case 'div': {
      const a = evC(t.a!, env, imag);
      const b = evC(t.b!, env, imag);
      return a && b ? cdiv(a, b) : null;
    }
    case 'neg': {
      const a = evC(t.a!, env, imag);
      return a ? [-a[0], -a[1]] : null;
    }
    case 'sq': {
      const a = evC(t.a!, env, imag);
      return a ? cmul(a, a) : null;
    }
    case 'pow': {
      const a = evC(t.a!, env, imag);
      const e = R(t.b!);
      if (!a || e === null) return null;
      if (Number.isInteger(e)) return cpowInt(a, e);
      const ar = real(a);
      return ar !== null && ar >= 0 ? [Math.pow(ar, e), 0] : null;
    }
    case 'exp': {
      const a = evC(t.a!, env, imag);
      if (!a) return null;
      const m = Math.exp(a[0]);
      return [m * Math.cos(a[1]), m * Math.sin(a[1])];
    }
    case 'abs': {
      // module (moteur v11) : |a + bi| = √(a² + b²)
      const a = evC(t.a!, env, imag);
      return a ? [Math.hypot(a[0], a[1]), 0] : null;
    }
    case 'arg': {
      // argument principal, dans ]−π ; π]
      const a = evC(t.a!, env, imag);
      if (!a || (Math.abs(a[0]) < 1e-12 && Math.abs(a[1]) < 1e-12)) return null;
      return [Math.atan2(a[1], a[0]), 0];
    }
    case 'sqrt':
    case 'cos':
    case 'sin':
    case 'tan':
    case 'arccos':
    case 'arcsin':
    case 'arctan':
    case 'ln':
    case 'log':
    case 'deg':
    case 'pct':
    case 'fact': {
      const a = R(t.a!);
      if (a === null) return null;
      const v = ev({ ...t, a: { k: 'n', v: a } }, {});
      return Number.isFinite(v) ? [v, 0] : null;
    }
  }
  return null;
}
const cEq = (a: Cx | null, b: Cx | null): boolean =>
  !!a && !!b && close(a[0], b[0]) && close(a[1], b[1]);

/** texte d'un complexe : « 16 », « 2i », « 1 − i », « −3 + 2i » */
export function cxText(v: Cx): string {
  const r = Math.abs(v[0]) < 1e-9 ? 0 : v[0];
  const m = Math.abs(v[1]) < 1e-9 ? 0 : v[1];
  const im = (x: number) => (close(Math.abs(x), 1) ? '' : formatNumber(Math.abs(x))) + 'i';
  if (!m) return formatNumber(r);
  if (!r) return (m < 0 ? '−' : '') + im(m);
  return formatNumber(r) + (m < 0 ? ' − ' : ' + ') + im(m);
}

// ── formes ─────────────────────────────────────────────────────────────────

function hasI(t: Node): boolean {
  return (t.k === 's' && t.n === IMAG) || kids(t).some(hasI);
}
/** un complexe dans l'expression : i, ou une grandeur complexe nommée (z, j) */
function hasC(cs: ComplexState, t: Node): boolean {
  if (t.k === 's') return t.n === IMAG || Array.isArray(cs.env[t.n!]);
  return kids(t).some((c) => hasC(cs, c));
}
function hasLetter(t: Node, env: CEnv): boolean {
  if (t.k === 's') return t.n !== IMAG && env[t.n!] === undefined && CONSTS[t.n!] === undefined;
  return kids(t).some((c) => hasLetter(c, env));
}
/** un nombre écrit : 3, −2, 2,5 */
function isNumber(t: Node): boolean {
  return t.k === 'n' || (t.k === 'neg' && t.a!.k === 'n');
}
/** un réel écrit simplement : 3, −2, √3, 2√3, √3/2, 1/2, 2^1013 */
function isRealClosed(t: Node): boolean {
  if (isNumber(t)) return true;
  if (t.k === 's') return CONSTS[t.n!] !== undefined;
  if (t.k === 'neg') return isRealClosed(t.a!);
  if (t.k === 'sqrt') return t.a!.k === 'n';
  if (t.k === 'pow') return t.a!.k === 'n' && t.b!.k === 'n';
  if (t.k === 'mul') return t.c!.length <= 3 && t.c!.every(isRealClosed);
  if (t.k === 'div') return isRealClosed(t.a!) && isNumber(t.b!);
  return false;
}
/** b × i écrit : i, 2i, −i, i√3, (√3/2) i, i√3/2 */
function isImagTerm(t: Node): boolean {
  if (t.k === 's') return t.n === IMAG;
  if (t.k === 'neg') return isImagTerm(t.a!);
  if (t.k === 'div') return isImagTerm(t.a!) && isNumber(t.b!);
  if (t.k === 'mul') {
    const ims = t.c!.filter((c) => c.k === 's' && c.n === IMAG);
    return (
      ims.length === 1 && t.c!.length <= 3 && t.c!.every((c) => c === ims[0] || isRealClosed(c))
    );
  }
  return false;
}
/** forme algébrique écrite : a, bi, a + bi (a, b réels écrits simplement) — la réponse attendue */
export function isAlgebraic(t: Node): boolean {
  if (isRealClosed(t) || isImagTerm(t)) return true;
  if (t.k === 'add' && t.c!.length === 2) {
    const [a, b] = t.c!;
    return (isRealClosed(a!) && isImagTerm(b!)) || (isRealClosed(b!) && isImagTerm(a!));
  }
  // (1 + i√3) ÷ 2 : un complexe écrit a + bi, divisé par un nombre
  if (t.k === 'div') return isNumber(t.b!) && t.a!.k === 'add' && isAlgebraic(t.a!);
  return false;
}
/** forme exponentielle écrite : e^(iθ), r × e^(iθ) (r réel écrit simplement) */
export function isExponential(t: Node): boolean {
  const isE = (x: Node) => x.k === 'exp' && isImagTerm(x.a!);
  if (isE(t)) return true;
  return (
    t.k === 'mul' &&
    t.c!.length === 2 &&
    t.c!.some(isE) &&
    t.c!.some((c) => !hasI(c) && isRealClosed(c))
  );
}
function opCount(t: Node): number {
  return t.k === 'n' || t.k === 's' ? 0 : 1 + kids(t).reduce((s, c) => s + opCount(c), 0);
}
/** z^n (z complexe, n entier) : [z, n] */
function powParts(t: Node): [Node, number] | null {
  if (t.k === 'sq') return [t.a!, 2];
  if (t.k === 'pow' && t.b!.k === 'n' && Number.isInteger(t.b!.v)) return [t.a!, t.b!.v!];
  return null;
}
function factorsOf(t: Node): Node[] {
  return t.k === 'mul' ? t.c! : [t];
}
const keyOf = (t: Node): string => canon(t);

// ── classes d'égalité (lignes établies) ───────────────────────────────────

function find(cs: ComplexState, k: string): string {
  let r = k;
  while (cs.parent.get(r) !== r) r = cs.parent.get(r)!;
  return r;
}
function addNode(cs: ComplexState, t: Node): string {
  const k = keyOf(t);
  if (!cs.parent.has(k)) {
    cs.parent.set(k, k);
    cs.nodes.set(k, t);
  }
  return k;
}
function union(cs: ComplexState, a: Node, b: Node): boolean {
  const ra = find(cs, addNode(cs, a));
  const rb = find(cs, addNode(cs, b));
  if (ra === rb) return false;
  cs.parent.set(ra, rb);
  return true;
}
function sameClass(cs: ComplexState, a: Node, b: Node): boolean {
  const ka = keyOf(a);
  const kb = keyOf(b);
  return cs.parent.has(ka) && cs.parent.has(kb) && find(cs, ka) === find(cs, kb);
}
function cloneState(cs: ComplexState): ComplexState {
  return { ...cs, parent: new Map(cs.parent), nodes: new Map(cs.nodes), facts: cs.facts.slice() };
}
/**
 * Un départ possible : l'expression apparaît déjà, ou elle est construite sur
 * des morceaux connus ((1 + i)² quand la question parle de (1 + i)⁸).
 */
function startable(cs: ComplexState, t: Node): boolean {
  if (known(cs, t)) return true;
  if (t.k === 's' && cs.defs.includes(t.n!)) return true;
  const p = powParts(t);
  if (p) return hasC(cs, p[0]) && keyOf(p[0]) !== IMAG && known(cs, p[0]);
  if (t.k === 'mul' || t.k === 'div')
    return (
      kids(t).some((c) => hasC(cs, c)) && kids(t).every((c) => !hasC(cs, c) || startable(cs, c))
    );
  return false;
}
/** l'expression apparaît-elle déjà (cible, énoncé, lignes écrites), en entier ou en morceau ? */
function known(cs: ComplexState, t: Node): boolean {
  const k = keyOf(t);
  if (cs.parent.has(k)) return true;
  const inside = (n: Node): boolean => keyOf(n) === k || kids(n).some(inside);
  if (inside(cs.target)) return true;
  for (const n of cs.nodes.values()) if (inside(n)) return true;
  return false;
}

// ── un pas ─────────────────────────────────────────────────────────────────

const L_IDENT = 'Développer, réduire';
const L_IPOW = 'Puissance de i';
const L_SPLIT = 'Découper la puissance';
const L_PRODPOW = 'Puissance d’un produit';
const L_CALC = 'Calculer';
const L_PRODUCT = 'Produit de deux complexes';
const L_TRIG = 'Forme trigonométrique';
const L_MOIVRE = 'Formule de Moivre';
const L_EULER = 'Formule d’Euler';
const L_MOD = 'Module';
const L_MODPROP = 'Module d’un produit, d’un quotient, d’une puissance';
const L_ARG = 'Argument';
const L_PERIOD = 'Retirer des tours (2π)';
const L_DEF = 'Formule';
const L_SUBST = 'Remplacer par un résultat établi';

/** identité vraie pour i quelconque (i traité comme une lettre) : développer, regrouper… */
function polyIdentity(cs: ComplexState, A: Node, B: Node): boolean {
  // module 1 ou petit, différentes de ±i ; une valeur qui déborde (z²⁰²⁶) est ignorée
  const samples: Cx[] = [
    [0.6, 0.8],
    [-0.28, 0.96],
    [0.8, -0.6],
    [0.03, 0.04],
    [-0.02, 0.05],
  ];
  // les grandeurs complexes nommées (z, j) sont aussi des inconnues : valeurs d'essai
  const named = Object.keys(cs.env).filter((n) => Array.isArray(cs.env[n]));
  let finite = 0;
  for (let si = 0; si < samples.length; si++) {
    const z = samples[si]!;
    const env: CEnv = { ...cs.env };
    named.forEach((n, k) => (env[n] = samples[(si + k + 1) % samples.length]!));
    const a = evC(A, env, z);
    const b = evC(B, env, z);
    const ok = (v: Cx | null) => !!v && Number.isFinite(v[0]) && Number.isFinite(v[1]);
    if (!ok(a) || !ok(b)) continue;
    if (!cEq(a, b)) return false;
    finite++;
  }
  return finite >= 2;
}
/** une puissance de i : i², i⁴, i³… */
function isIPow(t: Node): boolean {
  const p = powParts(t);
  return !!p && p[0].k === 's' && p[0].n === IMAG;
}
/** forme trigonométrique ou exponentielle : r(cos θ + i sin θ), e^(iθ), r e^(iθ) */
function isPolar(t: Node): boolean {
  const s = textOf(t);
  return (/cos/.test(s) && /sin/.test(s)) || isExponential(t);
}
function classHasPolar(cs: ComplexState, t: Node): boolean {
  const k = keyOf(t);
  if (!cs.parent.has(k)) return false;
  const root = find(cs, k);
  for (const [kk, n] of cs.nodes) if (find(cs, kk) === root && isPolar(n)) return true;
  return false;
}
/** |z₁z₂| = |z₁||z₂|, |zⁿ| = |z|ⁿ, |z₁/z₂| = |z₁|/|z₂| (X sous la barre, B le membre écrit) */
function absSplit(X: Node, B: Node): boolean {
  const absOf = (t: Node) => (t.k === 'abs' ? keyOf(t.a!) : null);
  if (X.k === 'mul' && B.k === 'mul' && X.c!.length === B.c!.length) {
    const xs = X.c!.map(keyOf).sort().join('|');
    const bs = B.c!.map(absOf);
    return !bs.includes(null) && (bs as string[]).sort().join('|') === xs;
  }
  const px = powParts(X);
  const pb = powParts(B);
  if (px && pb) return px[1] === pb[1] && absOf(pb[0]) === keyOf(px[0]);
  if (X.k === 'div' && B.k === 'div')
    return absOf(B.a!) === keyOf(X.a!) && absOf(B.b!) === keyOf(X.b!);
  return false;
}
/** le maillon A = B se justifie-t-il en un pas ? Nom du pas, '' si identique, null sinon. */
export function complexStep(cs: ComplexState, A: Node, B: Node, depth = 0): string | null {
  if (depth > 10) return null;
  if (keyOf(A) === keyOf(B)) return '';
  const vA = evC(A, cs.env);
  const vB = evC(B, cs.env);
  if (!cEq(vA, vB)) return null;
  if (sameClass(cs, A, B)) return L_SUBST;
  // grandeur définie par une formule : Δ = 2² − 4 × 1 × 5 (la formule, pas le résultat)
  if (A.k === 's' && cs.defs.includes(A.n!) && !isNumber(B)) return L_DEF;
  // sous un « − » : −j³ = −1 se justifie comme j³ = 1
  if (A.k === 'neg' && (B.k === 'neg' || (B.k === 'n' && B.v! < 0)))
    return complexStep(cs, A.a!, B.k === 'neg' ? B.a! : { k: 'n', v: -B.v! }, depth + 1);
  // nombres réels : on calcule (16 × 1 = 16, (√2)⁸ = 16, cos(2π) = 1)
  if (!hasC(cs, A) && !hasC(cs, B)) return opCount(B) < opCount(A) || isNumber(B) ? L_CALC : null;
  // module : |a + bi| = √(a² + b²) (écrit avec la formule), |r e^(iθ)| = r, |z₁z₂| = |z₁||z₂|…
  if (A.k === 'abs') {
    const X = A.a!;
    if (!hasC(cs, B) && /√|²|\^/.test(textOf(B)) && isAlgebraic(X)) return L_MOD;
    if (!hasC(cs, B) && isRealClosed(B) && (isPolar(X) || classHasPolar(cs, X))) return L_MOD;
    if (absSplit(X, B)) return L_MODPROP;
  }
  // argument lu sur une forme trigonométrique ou exponentielle (écrite ou établie)
  if (A.k === 'arg' && !hasC(cs, B) && (isPolar(A.a!) || classHasPolar(cs, A.a!))) return L_ARG;
  // e^(iθ) = e^(i(θ − 2kπ)) : on retire des tours complets
  if (A.k === 'exp' && B.k === 'exp' && isImagTerm(A.a!) && isImagTerm(B.a!)) return L_PERIOD;
  // i² = −1, i⁴ = 1 (B écrit sans puissance)
  // (aussi précédée d'un « − » : −i² = 1)
  const A1 = A.k === 'neg' ? A.a! : A;
  if (isIPow(A1) && powParts(A1)![1] <= 4 && (isNumber(B) || isImagTerm(B))) return L_IPOW;
  // e^(2iπ) = 1, cos(π/4) + i sin(π/4) écrit en a + bi : valeur exacte calculée
  // cos(π/4) = √2/2 : valeur exacte ; e^(2ikπ) = 1 (sinon Euler : cos θ + i sin θ)
  if ((A.k === 'cos' || A.k === 'sin') && isAlgebraic(B)) return L_CALC;
  if (A.k === 'exp' && isNumber(B) && cEq(vB, [1, 0])) return L_CALC;
  // formule d'Euler : e^(iθ) = cos θ + i sin θ (aussi r e^(iθ) = r(cos θ + i sin θ))
  {
    const sa = textOf(A);
    const sb = textOf(B);
    if (/exp/.test(sa) && !/exp/.test(sb) && /cos/.test(sb) && /sin/.test(sb) && !powParts(A))
      return L_EULER;
  }
  // 2⁴ × i⁴ = 16 : des nombres et des puissances de i, calculés
  if (
    A1.k === 'mul' &&
    A1.c!.every((c) => !hasI(c) || isIPow(c) || (c.k === 's' && c.n === IMAG)) &&
    isAlgebraic(B)
  )
    return L_CALC;
  // forme trigonométrique / exponentielle : 1 + i = √2(cos(π/4) + i sin(π/4))
  const trigShape = (t: Node) => {
    const s = textOf(t);
    return (/cos/.test(s) && /sin/.test(s)) || /exp/.test(s);
  };
  if (!trigShape(A) && trigShape(B) && isAlgebraic(A)) return L_TRIG;
  // même forme pour une puissance ou un quotient : morceau par morceau ((1 + i)⁸ = (√2(…))⁸)
  if ((A.k === 'pow' || A.k === 'div') && A.k === B.k) {
    const s1 = complexStep(cs, A.a!, B.a!, depth + 1);
    const s2 = s1 === null ? null : complexStep(cs, A.b!, B.b!, depth + 1);
    if (s1 !== null && s2 !== null && (s1 || s2)) return s1 || s2;
  }
  // développer, factoriser, regrouper : vrai quel que soit i
  if (polyIdentity(cs, A, B)) {
    // fusionner des exponentielles : on écrit d'abord la somme des arguments, e^(i(θ₁ + θ₂))
    const exps = (t: Node): Node[] => (t.k === 'exp' ? [t] : kids(t).flatMap(exps));
    const ea = exps(A);
    const eb = exps(B);
    if (ea.length > eb.length && eb.length && eb.every((e) => isImagTerm(e.a!))) return null;
    // une puissance élevée d'un complexe ne se développe pas d'un coup (binôme : on découpe)
    const pa = powParts(A);
    if (pa && pa[1] > 3 && hasI(pa[0]) && B.k === 'add') return null;
    // z⁸ = (z²)⁴, z⁸ = z⁴ × z⁴, (2i)⁴ = 2⁴ × i⁴ : réécritures de puissances
    if (pa && (powParts(B) || B.k === 'mul'))
      return factorsOf(A).length > 1 || (pa[0].k === 'mul' && B.k === 'mul') ? L_PRODPOW : L_SPLIT;
    return L_IDENT;
  }
  // (1 + i)² = 2i, (1 + i)(1 − i) = 2 : produit de deux complexes écrits a + bi
  const pa = powParts(A);
  // (pas pour l'expression de la question : c'est elle qu'on demande de développer)
  const isTarget = keyOf(A) === keyOf(cs.target);
  if (!isTarget && pa && pa[1] === 2 && isAlgebraic(pa[0]) && isAlgebraic(B)) return L_PRODUCT;
  if (!isTarget && A.k === 'mul' && A.c!.length === 2 && A.c!.every(isAlgebraic) && isAlgebraic(B))
    return L_PRODUCT;
  // Moivre : (r(cos θ + i sin θ))ⁿ = rⁿ(cos nθ + i sin nθ)
  if (pa && trigShape(pa[0]) && trigShape(B) && !powParts(B)) return L_MOIVRE;
  // même forme : morceau par morceau (plusieurs morceaux peuvent changer)
  if (A.k === B.k && kids(A).length === kids(B).length && kids(A).length) {
    const ka = kids(A);
    const kb = kids(B);
    if (A.c) {
      const used = new Set<number>();
      const labels: string[] = [];
      for (const x of ka) {
        let found = -1;
        for (let j = 0; j < kb.length && found < 0; j++) {
          if (used.has(j)) continue;
          const s = complexStep(cs, x, kb[j]!, depth + 1);
          if (s !== null) {
            found = j;
            if (s) labels.push(s);
          }
        }
        if (found < 0) return null;
        used.add(found);
      }
      return labels[0] ?? '';
    }
    const labels: string[] = [];
    for (let j = 0; j < ka.length; j++) {
      const s = complexStep(cs, ka[j]!, kb[j]!, depth + 1);
      if (s === null) return null;
      if (s) labels.push(s);
    }
    return labels[0] ?? '';
  }
  // un morceau d'une somme ou d'un produit remplacé (1 + 2i + i² = 1 + 2i − 1)
  if ((A.k === 'add' || A.k === 'mul') && A.k === B.k) {
    const kb = B.c!;
    for (let i = 0; i < A.c!.length; i++) {
      const rest = A.c!.filter((_, j) => j !== i);
      const restKeys = rest.map(keyOf).sort().join('|');
      // B = rest + (un ou plusieurs termes qui remplacent A.c[i])
      const bRest = kb.filter((t) => rest.some((r) => keyOf(r) === keyOf(t)));
      if (bRest.map(keyOf).sort().join('|') !== restKeys) continue;
      const repl = kb.filter((t) => !bRest.includes(t));
      if (!repl.length) continue;
      const sub = repl.length === 1 ? repl[0]! : { k: A.k, c: repl };
      const s = complexStep(cs, A.c![i]!, sub, depth + 1);
      if (s !== null) return s;
    }
  }
  return null;
}

// ── état ───────────────────────────────────────────────────────────────────

export function createComplexState(model: ScratchModel, spec: ComplexSpec): ComplexState {
  const env: CEnv = {};
  Object.entries(model.symbols).forEach(([n, s]) => {
    if (n !== IMAG && s.given && s.value !== undefined) env[n] = s.value;
  });
  // grandeurs complexes nommées par l'énoncé : « z = 1 + i », « j = exp(i × 2π ÷ 3) »
  [...(model.givens || []), ...(model.admitted || [])].forEach((g) => {
    const d = parseText(g).dis[0];
    if (!d || d.sides.length !== 2) return;
    [0, 1].forEach((si) => {
      const s0 = d.sides[si]!;
      if (s0.k !== 's' || s0.n === IMAG || env[s0.n!] !== undefined) return;
      const v = evC(d.sides[1 - si]!, env);
      if (v) env[s0.n!] = v;
    });
  });
  // grandeurs définies par une formule (Δ = b² − 4ac) : connues du moteur
  Object.entries(spec.defs ?? {}).forEach(([n, e]) => {
    const v = evC(parseText(e).dis[0]!.sides[0]!, env);
    if (v) env[n] = real(v) ?? v;
  });
  const given = parseText(spec.target).dis[0]!;
  const target = given.sides[0]!;
  const sols = (spec.solutions ?? [])
    .map((e) => evC(parseText(e).dis[0]!.sides[0]!, env))
    .filter((v): v is Cx => !!v);
  const cs: ComplexState = {
    target,
    form: spec.form ?? 'algebraic',
    value: spec.unknown ? (sols[0] ?? [NaN, NaN]) : (evC(target, env) ?? [NaN, NaN]),
    env,
    parent: new Map(),
    nodes: new Map(),
    facts: [],
    sols,
    eqLines: [],
    defs: Object.keys(spec.defs ?? {}),
  };
  if (spec.unknown) {
    cs.unknown = spec.unknown;
    cs.eqLines.push({ dis: [given] });
  }
  addNode(cs, target);
  [...(model.givens || []), ...(model.admitted || [])].forEach((g) =>
    addComplexFact(cs, parseText(g).dis[0]!),
  );
  return cs;
}

/** enregistre une ligne validée : une équation en z, ou une chaîne de calcul */
export function commitComplexLine(cs: ComplexState, line: Line): void {
  if (!mentionsUnknown(cs, line)) {
    addComplexFact(cs, line.dis[0]!);
    return;
  }
  cs.eqLines.push(line);
  // les membres sans z d'une chaîne (z = A = B) sont égaux entre eux
  line.dis.forEach((d) => {
    const rest = d.sides.filter((x) => !hasUnknownNode(cs, x));
    if (rest.length > 1) addComplexFact(cs, { sides: rest });
  });
}
export function addComplexFact(cs: ComplexState, eq: Eq): number {
  let merged = 0;
  for (let i = 0; i + 1 < eq.sides.length; i++)
    if (union(cs, eq.sides[i]!, eq.sides[i + 1]!)) merged++;
  cs.facts.push(eq);
  return merged;
}

/** la forme attendue : a + bi (pas une fraction de complexes), ou r e^(iθ) */
function fits(cs: ComplexState, n: Node): boolean {
  if (n.k === 's' && Array.isArray(cs.env[n.n!])) return true;
  return cs.form === 'exponential' ? isExponential(n) : isAlgebraic(n);
}
/** la cible est-elle égale à une forme algébrique écrite (a + bi, ou un réel) ? */
export function complexGoalReached(cs: ComplexState): boolean {
  if (cs.unknown) return cs.eqLines.slice(1).some((L) => solvesAll(cs, L));
  const kt = keyOf(cs.target);
  if (!cs.parent.has(kt)) return false;
  const root = find(cs, kt);
  for (const [k, n] of cs.nodes)
    if (k !== kt && find(cs, k) === root && fits(cs, n) && cEq(evC(n, cs.env), cs.value))
      return true;
  return false;
}

/** valeurs réelles écrites pour la cible (réponses trouvées) */
export function complexStatedValues(cs: ComplexState): number[] {
  const kt = keyOf(cs.target);
  if (!cs.parent.has(kt)) return [];
  const root = find(cs, kt);
  const out: number[] = [];
  cs.nodes.forEach((n, k) => {
    if (find(cs, k) !== root || !isAlgebraic(n)) return;
    const v = real(evC(n, cs.env));
    if (v !== null) out.push(v);
  });
  return out;
}

/** la ligne est-elle vraie (chaque maillon, en calcul complexe) ? */
export function complexIsTrue(cs: ComplexState, line: Line): boolean {
  if (mentionsUnknown(cs, line)) return eqTrue(cs, line);
  return line.dis.every((d) => {
    for (let i = 0; i + 1 < d.sides.length; i++)
      if (!cEq(evC(d.sides[i]!, cs.env), evC(d.sides[i + 1]!, cs.env))) return false;
    return true;
  });
}

/** valeur complexe d'un texte (choix du QCM « 16i ») ; null s'il ne se lit pas */
export function complexValueOf(cs: ComplexState, text: string): Cx | null {
  try {
    return evC(parseText(text).dis[0]!.sides[0]!, cs.env);
  } catch {
    return null;
  }
}
export function complexEquals(a: Cx | null, b: Cx | null): boolean {
  return cEq(a, b);
}

// ── juge ───────────────────────────────────────────────────────────────────

export function complexJudge(
  cs: ComplexState,
  model: ScratchModel,
  chips: Chip[],
  opts?: { verify?: boolean },
): ComplexVerdict {
  const norm = normalizeChips(chips);
  const A = norm.atoms;
  if (!A.length) return { kind: 'empty', atoms: A, badChip: -1 };
  const syn = checkSyntax(A);
  if (!syn.ok)
    return {
      kind: 'syntax',
      atoms: A,
      title: 'Ça commence mal',
      message: syn.msg,
      badChip: norm.src[syn.index] !== undefined ? norm.src[syn.index]! : chips.length - 1,
    };
  if (!syn.complete || !syn.hasEq) return { kind: 'open', atoms: A, complete: false };
  let E: Line;
  try {
    E = parseLine(A);
  } catch {
    return { kind: 'open', atoms: A, complete: false };
  }
  const v = evaluate(cs, model, E, A);
  if (!opts?.verify && (v.kind === 'unjustified' || v.kind === 'false'))
    return { kind: 'pending', atoms: A, complete: true };
  return v;
}

function trapFor(model: ScratchModel, E: Line): { line: Line; title: string; why: string } | null {
  const k = lineKey(E);
  for (const t of model.traps || []) {
    try {
      const tl = parseText(t.line);
      if (lineKey(tl) === k) return { line: tl, title: t.title, why: t.why };
    } catch {
      /* piège illisible : ignoré */
    }
  }
  return null;
}
function lineKey(L: Line): string {
  return L.dis.map((d) => d.sides.map(keyOf).join('=')).join('|');
}

function evaluate(cs: ComplexState, model: ScratchModel, E: Line, A: string[]): ComplexVerdict {
  if (mentionsUnknown(cs, E) && !E.dis.some((d) => d.cmp || d.rels))
    return evaluateEq(cs, model, E, A);
  if (E.dis.length !== 1 || E.dis[0]!.cmp || E.dis[0]!.rels)
    return {
      kind: 'unjustified',
      atoms: A,
      title: 'Une égalité à la fois',
      message: 'Écris une seule égalité (ou une chaîne de « = ») par ligne.',
    };
  const eq = E.dis[0]!;
  const trap = trapFor(model, E);
  // chaque maillon juste
  for (let i = 0; i + 1 < eq.sides.length; i++) {
    const a = eq.sides[i]!;
    const b = eq.sides[i + 1]!;
    const va = evC(a, cs.env);
    const vb = evC(b, cs.env);
    if (hasLetter(a, cs.env) || hasLetter(b, cs.env) || !va || !vb)
      return {
        kind: 'unjustified',
        atoms: A,
        title: 'Je ne sais pas calculer ça',
        message: 'Écris des calculs avec les nombres de la question et i.',
      };
    if (!cEq(va, vb)) {
      if (trap)
        return {
          kind: 'trap',
          atoms: A,
          title: 'Piège : ' + trap.title.charAt(0).toLowerCase() + trap.title.slice(1),
          message: trap.why,
          trap,
          badChip: -1,
        };
      return {
        kind: 'false',
        atoms: A,
        title: 'Ce n’est pas égal',
        message: `« ${textOf(a)} » vaut ${cxText(va)}, pas ${cxText(vb)}. Rappel : i² = −1.`,
      };
    }
  }
  // le départ : l'expression de la question, ou un résultat déjà écrit
  if (!startable(cs, eq.sides[0]!) && !startable(cs, eq.sides[eq.sides.length - 1]!))
    return {
      kind: 'unjustified',
      atoms: A,
      keep: { line: E, kind: 'aside' },
      title: 'D’où part ce calcul ?',
      message:
        'Pars de l’expression de la question (ou d’un morceau), ou d’un résultat que tu as déjà écrit.',
    };
  // chaque maillon justifié
  const labels: string[] = [];
  for (let i = 0; i + 1 < eq.sides.length; i++) {
    const a = eq.sides[i]!;
    const b = eq.sides[i + 1]!;
    const s = complexStep(cs, a, b);
    if (s === null) {
      const p = powParts(a);
      return {
        kind: 'unjustified',
        atoms: A,
        keep: { line: E, kind: 'jump' },
        title: 'D’où vient ce maillon ?',
        message:
          `« ${textOf(a)} = ${textOf(b)} » est vrai, mais c’est un trop grand saut. ` +
          (p && p[1] > 2
            ? 'Découpe la puissance (z⁸ = (z²)⁴) ou passe par la forme trigonométrique.'
            : 'Développe, puis remplace i² par −1, puis regroupe.'),
      };
    }
    if (s && !labels.includes(s)) labels.push(s);
  }
  const before = complexGoalReached(cs);
  const trial = cloneState(cs);
  const merged = addComplexFact(trial, eq);
  if (!merged)
    return {
      kind: 'useless',
      atoms: A,
      line: E,
      title: 'Juste, mais ça ne t’avance pas',
      message: 'Chaque maillon est juste, mais tu avais déjà tout ça.',
    };
  const final = !before && complexGoalReached(trial);
  const hora = Math.min(6, merged + 1);
  const label = labels.join(' → ') || L_CALC;
  return {
    kind: 'step',
    atoms: A,
    line: E,
    final,
    hora,
    rule: {
      id: 'complexe',
      card: '',
      label,
      hora,
      why: final
        ? 'C’est la forme algébrique a + bi : c’est la réponse.'
        : 'Chaque maillon est juste et justifié.',
    },
  };
}

// ── équations en z ─────────────────────────────────────────────────────────

const L_SAMEOP = 'Même opération des deux côtés';
const L_ROOTS = 'Racines carrées';
const L_FORMULA = 'Formule du second degré';
const L_ZERO = 'Produit nul';

function hasUnknownNode(cs: ComplexState, t: Node): boolean {
  if (!cs.unknown) return false;
  if (t.k === 's') return t.n === cs.unknown;
  return kids(t).some((c) => hasUnknownNode(cs, c));
}
function mentionsUnknown(cs: ComplexState, L: Line): boolean {
  return !!cs.unknown && L.dis.some((d) => d.sides.some((x) => hasUnknownNode(cs, x)));
}
/** valeur avec z remplacé par une valeur */
function evAt(cs: ComplexState, t: Node, z: Cx): Cx | null {
  return evC(t, { ...cs.env, [cs.unknown!]: z });
}
/** vraie pour chaque solution : une des alternatives a tous ses membres égaux */
function eqTrue(cs: ComplexState, L: Line): boolean {
  return cs.sols.every((z) =>
    L.dis.some((d) => {
      const v0 = evAt(cs, d.sides[0]!, z);
      return d.sides.every((x) => cEq(evAt(cs, x, z), v0));
    }),
  );
}
/** « z = E » (chaîne permise) : la valeur de la dernière écriture, sinon null */
function branchValue(cs: ComplexState, d: Eq): Node | null {
  const s0 = d.sides[0]!;
  if (s0.k !== 's' || s0.n !== cs.unknown || d.sides.length < 2) return null;
  const last = d.sides[d.sides.length - 1]!;
  return hasUnknownNode(cs, last) ? null : last;
}
/** la ligne donne toutes les solutions, chacune sous la forme a + bi */
function solvesAll(cs: ComplexState, L: Line): boolean {
  const vals = L.dis.map((d) => branchValue(cs, d));
  if (vals.some((v) => !v || !fits(cs, v))) return false;
  const vs = (vals as Node[]).map((v) => evC(v, cs.env));
  return (
    cs.sols.every((z) => vs.some((v) => cEq(v, z))) &&
    vs.every((v) => cs.sols.some((z) => cEq(v, z)))
  );
}
/** L2 − R2 = k(L1 − R1) pour une constante k ≠ 0 : même opération des deux côtés */
function sameOp(cs: ComplexState, a: Eq, b: Eq): boolean {
  if (a.sides.length < 2 || b.sides.length < 2) return false;
  const diff = (d: Eq, z: Cx) => {
    const l = evAt(cs, d.sides[0]!, z);
    const r = evAt(cs, d.sides[1]!, z);
    return l && r ? ([l[0] - r[0], l[1] - r[1]] as Cx) : null;
  };
  const zs: Cx[] = [
    [0.37, 0.81],
    [-1.2, 0.4],
    [0.9, -1.7],
    [2.3, 1.1],
  ];
  let k: Cx | null = null;
  let n = 0;
  for (const z of zs) {
    const da = diff(a, z);
    const db = diff(b, z);
    if (!da || !db || Math.hypot(da[0], da[1]) < 1e-9) continue;
    const r = cdiv(db, da);
    if (!r || !Number.isFinite(r[0])) return false;
    if (k && !cEq(k, r)) return false;
    k = k ?? r;
    n++;
  }
  return n >= 3 && !!k && Math.hypot(k[0], k[1]) > 1e-9;
}
/** une équation « X² = Y² » (Y sans z) : on prend les racines carrées */
function isSquareEq(cs: ComplexState, d: Eq): boolean {
  if (d.sides.length !== 2) return false;
  const sq = (t: Node) => powParts(t)?.[1] === 2;
  const [l, r] = d.sides as [Node, Node];
  return (
    (sq(l) && hasUnknownNode(cs, l) && sq(r) && !hasUnknownNode(cs, r)) ||
    (sq(r) && hasUnknownNode(cs, r) && sq(l) && !hasUnknownNode(cs, l))
  );
}
/** un produit de facteurs égal à 0 */
function isZeroProduct(cs: ComplexState, d: Eq): boolean {
  if (d.sides.length !== 2) return false;
  const zero = (t: Node) => t.k === 'n' && t.v === 0;
  const p = zero(d.sides[1]!) ? d.sides[0]! : zero(d.sides[0]!) ? d.sides[1]! : null;
  return !!p && p.k === 'mul' && p.c!.filter((c) => hasUnknownNode(cs, c)).length >= 2;
}

/** l'équation (ou l'alternative) est-elle vérifiée par z = v ? */
function satisfies(cs: ComplexState, d: Eq, v: Cx): boolean {
  const v0 = evAt(cs, d.sides[0]!, v);
  return d.sides.every((x) => cEq(evAt(cs, x, v), v0));
}
/**
 * Une alternative « … ou … » se justifie :
 *   • depuis une équation : produit nul (un facteur = 0 par alternative), racines carrées
 *     (z² = (3i)²), ou formule une fois Δ calculé ;
 *   • depuis une alternative déjà écrite : chaque morceau est gardé (même opération,
 *     calcul) ou résolu (racines carrées, formule avec Δ).
 */
function disjunctionLabel(cs: ComplexState, E: Line): string | null {
  const prev = cs.eqLines;
  const nb = E.dis.map((d) => ({ sides: d.sides.slice(0, 2) }) as Eq);
  const val = (d: Eq) => {
    const bv = branchValue(cs, d);
    return bv ? evC(bv, cs.env) : null;
  };
  const deltaDone = cs.defs.some((n) => cs.parent.has(n));
  const root = (d: Eq) => /√/.test(textOf(d.sides[1]!));
  // une équation du second degré (z², pas de z³) : la seule où la formule s'applique
  const u = cs.unknown!;
  const quadratic = (d: Eq) => {
    const t = d.sides.map(textOf).join(' = ');
    return new RegExp(u + '(²|\\^2)').test(t) && !new RegExp(u + '([³⁴⁵⁶]|\\^[3-9])').test(t);
  };
  for (const L of prev.filter((x) => x.dis.length === 1)) {
    const x = L.dis[0]!;
    if (isZeroProduct(cs, x)) {
      const prod = x.sides[0]!.k === 'mul' ? x.sides[0]! : x.sides[1]!;
      const fs = prod.c!.filter((c) => hasUnknownNode(cs, c));
      if (
        fs.length === nb.length &&
        fs.every((f) => nb.some((b) => sameOp(cs, { sides: [f, { k: 'n', v: 0 }] }, b)))
      )
        return L_ZERO;
    }
    if (isSquareEq(cs, x) && nb.every((b) => val(b))) return L_ROOTS;
    if (deltaDone && quadratic(x) && nb.every((b) => val(b)) && nb.some(root)) return L_FORMULA;
  }
  for (const P of prev.filter((x) => x.dis.length > 1)) {
    const used = new Set<number>();
    const labs: string[] = [];
    let ok = true;
    for (const pd of P.dis) {
      const ph: Eq = { sides: pd.sides.slice(0, 2) };
      const pv = branchValue(cs, pd);
      const kept = nb.findIndex(
        (b) =>
          sameOp(cs, ph, b) ||
          (!!pv && !!branchValue(cs, b) && complexStep(cs, pv, branchValue(cs, b)!) !== null),
      );
      if (kept >= 0) {
        used.add(kept);
        continue;
      }
      const sat = nb
        .map((b, k) => [b, k] as [Eq, number])
        .filter(([b]) => {
          const v = val(b);
          return !!v && satisfies(cs, ph, v);
        });
      const sq = isSquareEq(cs, ph);
      if (sat.length && (sq || (deltaDone && quadratic(ph) && sat.some(([b]) => root(b))))) {
        sat.forEach(([, k]) => used.add(k));
        labs.push(sq ? L_ROOTS : L_FORMULA);
        continue;
      }
      ok = false;
      break;
    }
    if (ok && used.size === nb.length) return labs[0] ?? L_CALC;
  }
  return null;
}

function evaluateEq(cs: ComplexState, model: ScratchModel, E: Line, A: string[]): ComplexVerdict {
  const trap = trapFor(model, E);
  if (!eqTrue(cs, E)) {
    if (trap)
      return {
        kind: 'trap',
        atoms: A,
        title: 'Piège : ' + trap.title.charAt(0).toLowerCase() + trap.title.slice(1),
        message: trap.why,
        trap,
        badChip: -1,
      };
    // une seule des solutions : « z = −1 + 2i » quand il y en a deux
    const bv = E.dis.length === 1 ? branchValue(cs, E.dis[0]!) : null;
    const v1 = bv ? evC(bv, cs.env) : null;
    if (v1 && cs.sols.length > 1 && cs.sols.some((z) => cEq(z, v1)))
      return {
        kind: 'false',
        atoms: A,
        title: 'Il manque une solution',
        message: `Cette équation a ${cs.sols.length} solutions : écris « ${cs.unknown} = … ou ${cs.unknown} = … ».`,
      };
    return {
      kind: 'false',
      atoms: A,
      title: 'Ce n’est pas juste',
      message:
        'Remplace ' +
        cs.unknown +
        ' par la solution : cette égalité ne tient pas. Rappel : i² = −1.',
    };
  }
  // une alternative « z = E » doit être une solution (aucune solution ajoutée)
  if (E.dis.length > 1) {
    for (const d of E.dis) {
      const bv = branchValue(cs, d);
      const v = bv ? evC(bv, cs.env) : null;
      if (bv && !cs.sols.some((z) => cEq(z, v)))
        return {
          kind: 'false',
          atoms: A,
          title: 'Ce n’est pas une solution',
          message: `« ${textOf(bv)} » ne vérifie pas l’équation.`,
        };
    }
  }
  const labels: string[] = [];
  const prev = cs.eqLines;
  const disLab = E.dis.length > 1 ? disjunctionLabel(cs, E) : null;
  for (const d of E.dis) {
    // le premier maillon (avec z) : une équation établie transformée, ou résolue
    const head: Eq = { sides: d.sides.slice(0, 2) };
    let lab: string | null = disLab;
    if (E.dis.length === 1 && prev.some((L) => L.dis.length === 1 && sameOp(cs, L.dis[0]!, head))) {
      // « z = 1 − 2i » d'emblée : on isole d'abord z (z = (3 − i) ÷ (1 + i)), puis on calcule
      const isolatedFinal = branchValue(cs, head) !== null && fits(cs, head.sides[1]!);
      const isolatedBefore = prev.some((L) => L.dis.length === 1 && branchValue(cs, L.dis[0]!));
      lab = !isolatedFinal || isolatedBefore ? L_SAMEOP : null;
    }
    if (!lab)
      return {
        kind: 'unjustified',
        atoms: A,
        keep: { line: E, kind: 'jump' },
        title: 'D’où vient cette équation ?',
        message:
          E.dis.length > 1
            ? 'Pour conclure, écris d’abord un carré (z² = (3i)²), un produit nul, ou calcule Δ et utilise la formule.'
            : 'Fais la même opération des deux côtés d’une équation déjà écrite.',
      };
    if (!labels.includes(lab)) labels.push(lab);
    // la suite de la chaîne : des calculs sans z
    for (let i = 1; i + 1 < d.sides.length; i++) {
      const s = complexStep(cs, d.sides[i]!, d.sides[i + 1]!);
      if (s === null)
        return {
          kind: 'unjustified',
          atoms: A,
          keep: { line: E, kind: 'jump' },
          title: 'D’où vient ce maillon ?',
          message: `« ${textOf(d.sides[i]!)} = ${textOf(d.sides[i + 1]!)} » est vrai, mais c’est un trop grand saut.`,
        };
      if (s && !labels.includes(s)) labels.push(s);
    }
  }
  if (prev.some((L) => lineKey(L) === lineKey(E)))
    return {
      kind: 'useless',
      atoms: A,
      line: E,
      title: 'Déjà écrit',
      message: 'Tu as déjà cette équation.',
    };
  const final = !complexGoalReached(cs) && solvesAll(cs, E);
  const hora = Math.min(6, 2 + E.dis.length);
  return {
    kind: 'step',
    atoms: A,
    line: E,
    final,
    hora,
    rule: {
      id: 'complexe',
      card: '',
      label: labels.join(' → '),
      hora,
      why: final
        ? 'Toutes les solutions, sous la forme a + bi : c’est la réponse.'
        : 'L’équation est transformée correctement.',
    },
  };
}

/** noyau (moteur v34) : la ligne, gardée, ferait-elle atteindre le but ? */
export function complexWouldReach(cs: ComplexState, line: Line): boolean {
  if (complexGoalReached(cs)) return false;
  const trial: ComplexState = { ...cloneState(cs), eqLines: cs.eqLines.slice() };
  commitComplexLine(trial, line);
  return complexGoalReached(trial);
}

// ── corrigé, coup de pouce ─────────────────────────────────────────────────

export function complexPathLines(spec: ComplexSpec): Line[] {
  return spec.path.map((p) => parseText(p));
}

/** la ligne est-elle déjà établie (tous ses maillons dans une même classe) ? */
export function complexEstablished(cs: ComplexState, L: Line): boolean {
  if (mentionsUnknown(cs, L)) return cs.eqLines.some((x) => lineKey(x) === lineKey(L));
  const d = L.dis[0]!;
  for (let i = 0; i + 1 < d.sides.length; i++)
    if (!sameClass(cs, d.sides[i]!, d.sides[i + 1]!)) return false;
  return true;
}
export function complexLineText(L: Line): string {
  return lineText(L);
}
export function complexLineAtoms(L: Line): string[] {
  return lineAtoms(L);
}
